if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      get loadingSpinner() {
        return this.submitButton?.querySelector('.loading__spinner') || this.querySelector('.loading__spinner');
      }

      setLoading(isLoading) {
        if (this.loadingTimeout) {
          clearTimeout(this.loadingTimeout);
          this.loadingTimeout = null;
        }

        this.submitButton.classList.toggle('loading', isLoading);
        this.submitButton.toggleAttribute('aria-busy', isLoading);
        this.submitButton.querySelectorAll('.loading__spinner').forEach((spinner) => {
          spinner.classList.toggle('hidden', !isLoading);
        });

        if (isLoading) {
          this.submitButton.setAttribute('aria-disabled', 'true');
          this.loadingTimeout = setTimeout(() => this.setLoading(false), 15000);
        } else {
          this.submitButton.removeAttribute('aria-disabled');
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();
        this.error = false;
        this.setLoading(true);

        this.addToCart()
          .then((response) => {
            const addedLineItem = this.hasAddedLineItem(response);

            if (!addedLineItem) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: this.variantIdInput?.value,
                errors: response?.errors || response?.description,
                message: response?.message,
              });
              this.error = true;
              this.cart?.classList.add('is-empty');
              this.handleErrorMessage(
                response?.description || response?.message || window.cartStrings?.error
              );
              return;
            }

            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: String(response.variant_id || response.id || this.variantIdInput?.value),
              cartData: response,
            }).then(() => {
              CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
            });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure('add:paint-updated-sections', () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure('add:paint-updated-sections', () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.error = true;
            this.cart?.classList.add('is-empty');
            this.handleErrorMessage(window.cartStrings?.error);
          })
          .finally(() => {
            this.setLoading(false);
            if (!this.error && this.cart) this.cart.classList.remove('is-empty');
            if (!this.error) {
              const quantityInput = this.form.querySelector('[name="quantity"]');
              let quantity = parseInt(quantityInput?.value || '1', 10);
              if (!Number.isFinite(quantity) || quantity < 1) quantity = 1;
              window.adjustYanaPreorderInventory?.(this.variantIdInput?.value, -quantity);
            }
            window.syncYanaPreorderButton?.(this.variantIdInput?.value);
            try {
              CartPerformance.measureFromEvent('add:user-action', evt);
            } catch (error) {
              /* ignore missing performance helper */
            }
          });
      }

      hasAddedLineItem(response) {
        if (!response || response.status) return false;
        return Boolean(response.id || response.product_id || response.key || response.variant_id);
      }

      nativeFetch(url, options) {
        return fetch(url, options);
      }

      async parseJsonResponse(response) {
        const text = await response.text();
        try {
          const json = JSON.parse(text);
          if (!response.ok && json && !json.status) json.status = response.status;
          return json;
        } catch (error) {
          return {
            status: response.status || 500,
            description: window.cartStrings?.error,
          };
        }
      }

      collectProperties() {
        const variantId = this.variantIdInput?.value;
        window.syncYanaPreorderButton?.(variantId);

        const properties = {};
        this.form.querySelectorAll('[name^="properties["]').forEach((input) => {
          if (input.disabled || input.value == null || input.value === '') return;
          const match = input.name.match(/^properties\[([^\]]+)\]$/);
          if (match) properties[match[1]] = input.value;
        });

        const fromMetafield = window.yanaPreorderLineProperties?.(variantId);
        if (fromMetafield) Object.assign(properties, fromMetafield);

        return Object.keys(properties).length ? properties : undefined;
      }

      buildCartAddBody() {
        const quantityInput = this.form.querySelector('[name="quantity"]');
        let quantity = parseInt(quantityInput?.value || '1', 10);
        if (!Number.isFinite(quantity) || quantity < 1) quantity = 1;

        const variantId = Number(this.variantIdInput?.value);
        const body = {
          id: variantId,
          quantity,
          sections: this.cart
            ? this.cart.getSectionsToRender().map((section) => section.id).join(',')
            : undefined,
          sections_url: window.location.pathname,
        };

        const properties = this.collectProperties();
        if (properties) body.properties = properties;

        return body;
      }

      async postCartAdd(body) {
        const response = await this.nativeFetch('/cart/add.js', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(body),
        });
        return this.parseJsonResponse(response);
      }

      async attachCartSections(payload) {
        if (!this.cart) return payload;
        if (payload.sections?.['cart-drawer'] && payload.sections?.['cart-icon-bubble']) return payload;

        payload.sections = payload.sections || {};
        const sectionIds = this.cart.getSectionsToRender().map((section) => section.id);
        await Promise.all(
          sectionIds.map(async (id) => {
            if (payload.sections[id]) return;
            const res = await this.nativeFetch(`${window.routes.cart_url}?section_id=${id}`);
            payload.sections[id] = await res.text();
          })
        );
        return payload;
      }

      async addToCart() {
        if (this.cart) this.cart.setActiveElement(document.activeElement);
        this.variantIdInput.disabled = false;

        let result = await this.postCartAdd(this.buildCartAddBody());

        if (!this.hasAddedLineItem(result)) {
          const cart = await this.nativeFetch('/cart.js').then((res) => res.json());
          if (cart?.item_count > 0 && cart.items?.length) {
            result = await this.attachCartSections({
              ...cart.items[0],
              id: cart.items[0].id,
              key: cart.items[0].key,
              variant_id: cart.items[0].variant_id,
            });
          }
        } else {
          result = await this.attachCartSections(result);
          await window.syncYanaCartPreorderMeta?.();
        }

        return result;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        const variantId = this.variantIdInput?.value;
        const isResetting = disable && text == null;

        if (isResetting) {
          this.submitButton.setAttribute('disabled', 'disabled');
          return;
        }

        const isPreorder = Boolean(window.syncYanaPreorderButton?.(variantId));
        if (isPreorder) {
          this.submitButton.removeAttribute('disabled');
          this.submitButton.removeAttribute('aria-disabled');
          if (this.submitButtonText) {
            this.submitButtonText.textContent =
              (window.variantStrings && window.variantStrings.preorder) || 'Preorder';
          }
          return;
        }

        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }

        window.syncYanaPreorderButton?.(variantId);
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
